import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import { useLanguage } from '../contexts/LanguageContext';
import { backendUrl } from '../config';
import './ClassroomsPage.css';

const ClassroomsPage = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState('join'); // 'create' or 'join'
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Check for payment success/cancel messages
  useEffect(() => {
    if (searchParams.get('payment_success') === '1') {
      setSuccess(t('classroomPaymentSuccess') || 'Payment successful! Your classroom has been created.');
      // Clear the URL parameter
      navigate('/classrooms', { replace: true });
    } else if (searchParams.get('payment_canceled') === '1') {
      setError(t('paymentCanceled') || 'Payment was canceled. Classroom was not created.');
      navigate('/classrooms', { replace: true });
    }
  }, [searchParams, navigate, t]);

  // Create classroom state
  const [classroomName, setClassroomName] = useState('');
  const [classroomPassword, setClassroomPassword] = useState('');
  const [classroomUsername, setClassroomUsername] = useState('');
  const [selectedPlan, setSelectedPlan] = useState('monthly'); // 'monthly', 'yearly', 'semester'
  const [selectedTier, setSelectedTier] = useState('basic'); // 'family' (≤5), 'basic' (≤35), 'standard' (>35)
  const [processingCheckout, setProcessingCheckout] = useState(false);

  // Join classroom state
  const [classroomCode, setClassroomCode] = useState('');
  const [joinPassword, setJoinPassword] = useState('');
  const [joinUsername, setJoinUsername] = useState('');

  const getPrice = () => {
    const tier = selectedTier;
    const plan = selectedPlan;

    if (tier === 'family') {
      if (plan === 'monthly') return t('priceFamilyMonthly') || '$4.99/month';
      if (plan === 'semester') return t('priceFamilySemester') || '$17.99 / 4 months';
      return t('priceFamilyYearly') || '$49.99/year';
    } else if (tier === 'basic') {
      if (plan === 'monthly') return t('priceBasicMonthly') || '$14.99/month';
      if (plan === 'semester') return t('priceBasicSemester') || '$54.99 / 4 months';
      return t('priceBasicYearly') || '$149.99/year';
    } else { // standard
      if (plan === 'monthly') return t('priceStandardMonthly') || '$49.99/month';
      if (plan === 'semester') return t('priceStandardSemester') || '$179.99 / 4 months';
      return t('priceStandardYearly') || '$499.99/year';
    }
  };

  const handleCreateClassroom = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setProcessingCheckout(true);

    try {
      const { data: { session } } = await supabase.auth.getSession();
      const accessToken = session?.access_token;

      if (!accessToken) {
        setError(t('pleaseLogIn') || 'Please log in');
        setProcessingCheckout(false);
        return;
      }

      if (!classroomName.trim()) {
        setError(t('pleaseEnterClassroomName') || 'Please enter a classroom name');
        setProcessingCheckout(false);
        return;
      }

      if (!classroomPassword.trim()) {
        setError(t('pleaseEnterClassroomPassword') || 'Please enter a classroom password');
        setProcessingCheckout(false);
        return;
      }

      if (!classroomUsername.trim()) {
        setError(t('pleaseEnterClassroomUsername') || 'Please enter a classroom username');
        setProcessingCheckout(false);
        return;
      }

      if (classroomUsername.trim().length > 100) {
        setError(t('classroomUsernameTooLong') || 'Classroom username cannot exceed 100 characters');
        setProcessingCheckout(false);
        return;
      }

      // Determine capacity and max_words based on tier
      let capacity, maxWords;
      if (selectedTier === 'family') {
        capacity = 5;
        maxWords = 600;
      } else if (selectedTier === 'basic') {
        capacity = 35;
        maxWords = 1200;
      } else { // standard
        capacity = 150;
        maxWords = 5000;
      }

      // Call backend endpoint to create checkout session
      const response = await fetch(`${backendUrl}/create-classroom-checkout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: classroomName.trim(),
          password: classroomPassword.trim(),
          username: classroomUsername.trim(),
          capacity: capacity,
          maxWords: maxWords,
          plan: selectedPlan,
          accessToken: accessToken
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || t('failedToCreateCheckout') || 'Failed to create checkout session');
      }

      // Redirect to Stripe checkout
      window.location.href = data.url;
    } catch (err) {
      console.error('Error creating classroom checkout:', err);
      setError(err.message || t('failedToCreateCheckout') || 'Failed to create checkout session');
      setProcessingCheckout(false);
    }
  };

  const handleJoinClassroom = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      const { data: { session } } = await supabase.auth.getSession();
      const accessToken = session?.access_token;

      if (!accessToken) {
        setError(t('pleaseLogIn') || 'Please log in');
        setLoading(false);
        return;
      }

      if (!classroomCode.trim()) {
        setError(t('pleaseEnterClassroomCode') || 'Please enter a classroom code');
        setLoading(false);
        return;
      }

      // Validate join code format (8 alphanumeric characters, uppercase)
      const joinCode = classroomCode.trim().toUpperCase();
      const joinCodeRegex = /^[A-Z0-9]{8}$/;
      if (!joinCodeRegex.test(joinCode)) {
        setError(t('invalidClassroomCode') || 'Invalid classroom code format. Code should be 8 characters.');
        setLoading(false);
        return;
      }

      if (!joinPassword.trim()) {
        setError(t('passwordRequired') || 'Password is required');
        setLoading(false);
        return;
      }

      if (!joinUsername.trim()) {
        setError(t('pleaseEnterClassroomUsername') || 'Please enter a classroom username');
        setLoading(false);
        return;
      }

      if (joinUsername.trim().length > 100) {
        setError(t('classroomUsernameTooLong') || 'Classroom username cannot exceed 100 characters');
        setLoading(false);
        return;
      }

      // Call backend endpoint to join classroom (password validation happens server-side)
      const response = await fetch(`${backendUrl}/join-classroom`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          joinCode: joinCode,
          password: joinPassword.trim(),
          username: joinUsername.trim(),
          accessToken: accessToken
        })
      });

      const data = await response.json();

      if (!response.ok) {
        // Map backend error codes to user-friendly messages
        if (response.status === 403) {
          setError(t('incorrectPassword') || 'Incorrect password');
        } else if (response.status === 404) {
          setError(t('invalidClassroomCode') || 'Invalid classroom code');
        } else if (response.status === 400 && data.error && data.error.includes('already a member')) {
          setError(t('alreadyMember') || 'You are already a member of this classroom');
        } else {
          setError(data.error || t('failedToJoinClassroom') || 'Failed to join classroom');
        }
        setLoading(false);
        return;
      }

      // Successfully joined
      const classroomName = data.classroom?.name || 'classroom';
      setSuccess(t('joinedClassroom') || `Successfully joined "${classroomName}"!`);
      setClassroomCode('');
      setJoinPassword('');
      setJoinUsername('');

      // Redirect to dashboard after 2 seconds
      setTimeout(() => {
        navigate('/');
      }, 2000);
    } catch (err) {
      console.error('Error joining classroom:', err);
      setError(err.message || t('failedToJoinClassroom') || 'Failed to join classroom');
    } finally {
      setLoading(false);
    }
  };



  return (
    <div className="classrooms-page">
      <div className="classrooms-container">
        <h1>{t('classrooms') || 'Classrooms'}</h1>

        <div className="classrooms-tabs">
          <button
            className={`classroom-tab ${activeTab === 'join' ? 'active' : ''}`}
            onClick={() => {
              setActiveTab('join');
              setError('');
              setSuccess('');
            }}
          >
            {t('joinClassroom') || 'Join Classroom'}
          </button>
          <button
            className={`classroom-tab ${activeTab === 'create' ? 'active' : ''}`}
            onClick={() => {
              setActiveTab('create');
              setError('');
              setSuccess('');
            }}
          >
            {t('createClassroom') || 'Create Classroom'}
          </button>
        </div>

        {error && (
          <div className="classroom-error">
            {error}
          </div>
        )}

        {success && (
          <div className="classroom-success">
            {success}
          </div>
        )}

        {activeTab === 'create' && (
          <div className="classroom-form-container">
            <form onSubmit={handleCreateClassroom} className="classroom-form">
              <div className="form-group">
                <label htmlFor="classroomName">
                  {t('classroomName') || 'Classroom Name'}
                </label>
                <input
                  id="classroomName"
                  type="text"
                  value={classroomName}
                  onChange={(e) => setClassroomName(e.target.value)}
                  placeholder={t('enterClassroomName') || 'Enter classroom name'}
                  maxLength={255}
                  required
                  disabled={processingCheckout}
                />
              </div>
              <div className="form-group">
                <label htmlFor="classroomPassword">
                  {t('classroomPassword') || 'Classroom Password'}
                </label>
                <input
                  id="classroomPassword"
                  type="password"
                  value={classroomPassword}
                  onChange={(e) => setClassroomPassword(e.target.value)}
                  placeholder={t('enterClassroomPassword') || 'Enter password'}
                  maxLength={255}
                  required
                  disabled={processingCheckout}
                />
                <p className="form-help-text" style={{ fontSize: '0.85em', color: '#666' }}>
                  {t('passwordRequiredHelp') || 'Students will need this password to join'}
                </p>
              </div>
              <div className="form-group">
                <label htmlFor="classroomUsername">
                  {t('classroomUsername') || 'Classroom Username'}
                </label>
                <input
                  id="classroomUsername"
                  type="text"
                  value={classroomUsername}
                  onChange={(e) => setClassroomUsername(e.target.value)}
                  placeholder={t('enterClassroomUsername') || 'Enter your classroom username'}
                  maxLength={100}
                  required
                  disabled={processingCheckout}
                />
                <p className="form-help-text" style={{ fontSize: '0.85em', color: '#666' }}>
                  {t('classroomUsernameHelp') || 'This is how you will appear to students in this classroom'}
                </p>
              </div>
              <div className="form-group">
                <label htmlFor="selectedTier">
                  {t('classroomTier') || 'Classroom Tier'}
                </label>
                <select
                  id="selectedTier"
                  value={selectedTier}
                  onChange={(e) => setSelectedTier(e.target.value)}
                  disabled={processingCheckout}
                  required
                >
                  <option value="family">{t('tierFamily') || 'Family (up to 5 members, 600 words/month)'}</option>
                  <option value="basic">{t('tierBasic') || 'Basic (up to 35 students, 1,200 words/month)'}</option>
                  <option value="standard">{t('tierStandard') || 'Standard (up to 150 students, 5,000 words/month)'}</option>
                </select>
              </div>
              <div className="form-group">
                <label htmlFor="selectedPlan">
                  {t('billingPlan') || 'Billing Plan'}
                </label>
                <select
                  id="selectedPlan"
                  value={selectedPlan}
                  onChange={(e) => setSelectedPlan(e.target.value)}
                  disabled={processingCheckout}
                  required
                >
                  <option value="monthly">{t('planMonthly') || 'Monthly'}</option>
                  <option value="semester">{t('planSemester') || 'Semester (4 months)'}</option>
                  <option value="yearly">{t('planYearly') || 'Yearly'}</option>
                </select>
              </div>
              <div style={{
                marginBottom: '1.5rem',
                padding: '1rem',
                backgroundColor: '#f8f9fa',
                borderRadius: '8px',
                border: '1px solid #dee2e6',
                textAlign: 'center'
              }}>
                <div style={{ fontSize: '0.9rem', color: '#666', marginBottom: '0.25rem' }}>
                  {t('totalPrice') || 'Total Price'}
                </div>
                <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#28a745' }}>
                  {getPrice()}
                </div>
              </div>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={processingCheckout || !classroomName.trim() || !classroomPassword.trim() || !classroomUsername.trim()}
              >
                {processingCheckout
                  ? (t('processing') || 'Processing...')
                  : (t('proceedToPayment') || 'Proceed to Payment')}
              </button>
              <p className="form-help-text" style={{ fontSize: '0.85em', color: '#666', marginTop: '10px' }}>
                {t('paymentRequiredNotice') || 'Payment is required to create a classroom. Your classroom will be created after successful payment.'}
              </p>
              <p className="form-help-text" style={{ fontSize: '0.8rem', color: '#888', fontStyle: 'italic', marginTop: '5px' }}>
                {t('recurringPaymentNotice') || 'Payments are recurring unless cancelled. You can cancel anytime from the classroom settings.'}
              </p>
            </form>
          </div>
        )}

        {activeTab === 'join' && (
          <div className="classroom-form-container">
            <form onSubmit={handleJoinClassroom} className="classroom-form">
              <div className="form-group">
                <label htmlFor="classroomCode">
                  {t('classroomCode') || 'Classroom Code'}
                </label>
                <input
                  id="classroomCode"
                  type="text"
                  value={classroomCode}
                  onChange={(e) => {
                    // Convert to uppercase and filter to alphanumeric only
                    const value = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '');
                    setClassroomCode(value);
                  }}
                  placeholder={t('enterClassroomCode') || 'Enter 8-character classroom code'}
                  maxLength={8}
                  style={{ textTransform: 'uppercase', fontFamily: 'monospace', letterSpacing: '0.1em' }}
                  required
                  disabled={loading}
                />
                <p className="form-help-text">
                  {t('getCodeFromTeacher') || 'Get the 8-character code from your teacher'}
                </p>
              </div>
              <div className="form-group">
                <label htmlFor="joinPassword">
                  {t('classroomPassword') || 'Classroom Password'}
                </label>
                <input
                  id="joinPassword"
                  type="password"
                  value={joinPassword}
                  onChange={(e) => setJoinPassword(e.target.value)}
                  placeholder={t('enterClassroomPassword') || 'Enter password'}
                  maxLength={255}
                  required
                  disabled={loading}
                />
                <p className="form-help-text" style={{ fontSize: '0.85em', color: '#666' }}>
                  {t('passwordRequiredHelp') || 'Enter the password provided by your teacher'}
                </p>
              </div>
              <div className="form-group">
                <label htmlFor="joinUsername">
                  {t('classroomUsername') || 'Classroom Username'}
                </label>
                <input
                  id="joinUsername"
                  type="text"
                  value={joinUsername}
                  onChange={(e) => setJoinUsername(e.target.value)}
                  placeholder={t('enterClassroomUsername') || 'Enter your classroom username'}
                  maxLength={100}
                  required
                  disabled={loading}
                />
                <p className="form-help-text" style={{ fontSize: '0.85em', color: '#666' }}>
                  {t('classroomUsernameHelp') || 'This is how you will appear to others in this classroom'}
                </p>
              </div>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={loading || !classroomCode.trim() || !joinUsername.trim()}
              >
                {loading ? (t('joining') || 'Joining...') : (t('joinClassroom') || 'Join Classroom')}
              </button>
            </form>
          </div>
        )}
      </div>
    </div>
  );
};

export default ClassroomsPage;

